// /empresas — landing + creación de pedido de tarjetas regalo para empresas (pago Stripe obligatorio).
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, Loader2, AlertCircle, Check, Ticket, CalendarDays, Gift, BookOpen } from 'lucide-react';
import { getCatalogoEmpresa, calcularPedidoEmpresa, crearCheckoutEmpresa } from '../lib/api';
import { formatEur } from '../components/CodigoDescuento';
import { tt } from '../lib/empresasTxt';

export default function Empresas() {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const T = (k, v) => tt(lang, k, v);
  const [params] = useSearchParams();
  const [cat, setCat] = useState(null);
  const [f, setF] = useState({ empresa_nombre: '', cif: '', contacto_nombre: '', contacto_email: '', tipo_destinatarios: 'empleados', producto_tipo: 'importe', importe_por_tarjeta: 50, num_clases: 5, bono_tipo_id: '', evento_id: '', unidades: 5, ocasion: 'navidad', mensaje: '' });
  const [calc, setCalc] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  useEffect(() => { getCatalogoEmpresa(lang).then(setCat).catch(() => {}); }, [lang]);
  const tipo = f.producto_tipo;
  useEffect(() => {
    const uni = parseInt(f.unidades, 10);
    const ok = uni >= 1 && uni <= 500 && (
      (tipo === 'importe' && Number(f.importe_por_tarjeta) >= 10 && Number(f.importe_por_tarjeta) <= 500) ||
      (tipo === 'clases' && parseInt(f.num_clases, 10) >= 1) ||
      (tipo === 'bono' && f.bono_tipo_id) || (tipo === 'evento' && f.evento_id));
    if (!ok) { setCalc(null); return undefined; }
    let vivo = true;
    const id = setTimeout(() => {
      calcularPedidoEmpresa({ producto_tipo: tipo, importe_por_tarjeta: f.importe_por_tarjeta, num_clases: f.num_clases, bono_tipo_id: f.bono_tipo_id, evento_id: f.evento_id, unidades: uni })
        .then((r) => { if (vivo) setCalc(r); }).catch(() => { if (vivo) setCalc(null); });
    }, 250);
    return () => { vivo = false; clearTimeout(id); };
  }, [tipo, f.importe_por_tarjeta, f.num_clases, f.bono_tipo_id, f.evento_id, f.unidades]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setEnviando(true);
    try {
      const r = await crearCheckoutEmpresa({ ...f, idioma: lang });
      if (r?.url) { window.location.href = r.url; return; }
      throw new Error('sin url');
    } catch (err) {
      setEnviando(false);
      setError(err?.message && err.status ? err.message : T('error'));
    }
  };

  const eur = (n) => formatEur(Number(n), lang);
  const TIPOS = [['clases', BookOpen], ['bono', Ticket], ['evento', CalendarDays], ['importe', Gift]];

  return (
    <main className="clase-page regalo-page">
      <div className="clase-page-inner">
        <div className="clase-page-head">
          <div className="clase-page-avatar"><Building2 size={32} strokeWidth={1.25} /></div>
          <div className="clase-page-head-info">
            <p className="eyebrow">{T('eyebrow')}</p>
            <h1 className="clase-page-title">{T('titulo')}</h1>
            <p className="clase-page-resumen">{T('sub')}</p>
            <p className="empresas-nota"><strong>{T('iva')}</strong></p>
          </div>
        </div>

        {params.get('stripe') === 'cancelled' && (
          <div className="evento-banner evento-banner--warn"><AlertCircle size={18} /> <span>{T('cancelado')}</span></div>
        )}

        <h2 className="empresas-h">{T('como_t')}</h2>
        <ol className="empresas-pasos">{['p1', 'p2', 'p3', 'p4'].map((k) => <li key={k}>{T(k)}</li>)}</ol>

        <div className="empresas-tramos">
          <div className="empresas-tramo"><strong>25 %</strong><span>{T('dto_regla')}</span></div>
        </div>

        <h2 className="empresas-h">{T('form_t')}</h2>
        <form className="modal-form regalo-form" onSubmit={submit}>
          <div className="modal-campo"><label>{T('empresa')}</label><input type="text" required maxLength={120} value={f.empresa_nombre} onChange={set('empresa_nombre')} /></div>
          <div className="modal-campo"><label>{T('cif')}</label><input type="text" required maxLength={20} value={f.cif} onChange={set('cif')} /></div>
          <div className="modal-campo"><label>{T('contacto')}</label><input type="text" maxLength={80} value={f.contacto_nombre} onChange={set('contacto_nombre')} /></div>
          <div className="modal-campo"><label>{T('email')}</label><input type="email" required value={f.contacto_email} onChange={set('contacto_email')} /></div>
          <div className="modal-campo"><label>{T('para')}</label>
            <div className="regalo-importes">
              {['empleados', 'clientes'].map((k) => (
                <button type="button" key={k} className={'regalo-chip' + (f.tipo_destinatarios === k ? ' activo' : '')} onClick={() => setF((x) => ({ ...x, tipo_destinatarios: k, ocasion: k === 'clientes' ? 'gracias' : 'navidad' }))}>{T(k)}</button>
              ))}
            </div>
          </div>
          <div className="modal-campo"><label>{T('ocasion')}</label>
            <div className="regalo-importes">
              {['navidad', 'gracias', 'especial'].map((k) => (
                <button type="button" key={k} className={'regalo-chip' + (f.ocasion === k ? ' activo' : '')} onClick={() => setF((x) => ({ ...x, ocasion: k }))}>{T(k)}</button>
              ))}
            </div>
          </div>
          <div className="modal-campo"><label>{T('tipo_t')}</label>
            <div className="empresas-tipos">
              {TIPOS.map(([k, Icono]) => (
                <button type="button" key={k} className={'empresas-tipo' + (tipo === k ? ' activo' : '')} onClick={() => setF((x) => ({ ...x, producto_tipo: k }))}>
                  <Icono size={20} strokeWidth={1.5} />
                  <strong>{T('t_' + k)}</strong>
                  <small>{T('t_' + k + '_d')}</small>
                </button>
              ))}
            </div>
          </div>
          {tipo === 'clases' && (
            <div className="modal-campo"><label>{T('num_clases')}</label>
              <input type="number" required min="1" max={cat?.clases_max || 50} step="1" value={f.num_clases} onChange={set('num_clases')} />
              {cat?.precio_clase > 0 && <small>{T('cada')} {eur((parseInt(f.num_clases, 10) || 0) * cat.precio_clase)}</small>}
            </div>
          )}
          {tipo === 'bono' && (
            <div className="modal-campo"><label>{T('elige_bono')}</label>
              {cat?.bonos?.length ? (
                <select required value={f.bono_tipo_id} onChange={set('bono_tipo_id')}>
                  <option value="">—</option>
                  {cat.bonos.map((b) => <option key={b.id} value={b.id}>{b.nombre} · {eur(b.precio_euros)}</option>)}
                </select>
              ) : <small>{T('sin_opciones')}</small>}
            </div>
          )}
          {tipo === 'evento' && (
            <div className="modal-campo"><label>{T('elige_evento')}</label>
              {cat?.eventos?.length ? (
                <select required value={f.evento_id} onChange={set('evento_id')}>
                  <option value="">—</option>
                  {cat.eventos.map((ev) => <option key={ev.id} value={ev.id}>{ev.nombre} · {ev.fecha} · {eur(ev.precio_euros)}</option>)}
                </select>
              ) : <small>{T('sin_opciones')}</small>}
            </div>
          )}
          {tipo === 'importe' && (
            <div className="modal-campo"><label>{T('importe')}</label><input type="number" required min="10" max="500" step="1" value={f.importe_por_tarjeta} onChange={set('importe_por_tarjeta')} /></div>
          )}
          <div className="modal-campo"><label>{T('unidades')}</label><input type="number" required min="1" max="500" step="1" value={f.unidades} onChange={set('unidades')} />
            <small>{T('oferta')}</small></div>
          <p className="empresas-nota">{T('equiv')}</p>
          <div className="modal-campo"><label>{T('mensaje')}</label><textarea rows={3} maxLength={300} value={f.mensaje} onChange={set('mensaje')} /></div>

          {calc && (
            <div className="empresas-resumen">
              <div><span>{T('valor')} ({parseInt(f.unidades, 10)} × {eur(calc.importe_por_tarjeta)})</span><span>{formatEur(calc.valor_total, lang)}</span></div>
              {calc.descuento_pct > 0 && <div><span>{T('descuento')} ({calc.descuento_pct} %)</span><span>− {formatEur(calc.descuento_euros, lang)}</span></div>}
              <div className="total"><span>{T('total')}</span><span>{formatEur(calc.a_pagar, lang)}</span></div>
              <p className="empresas-siguiente">{T('iva')}</p>
              {calc.faltan_para_descuento > 0 && <p className="empresas-siguiente"><Check size={14} /> {T('faltan_n', { n: calc.faltan_para_descuento })}</p>}
            </div>
          )}

          {error && <div className="modal-error-general"><AlertCircle size={16} /> {error}</div>}
          <p className="empresas-nota"><strong>{T('iva')}</strong> {T('nota_pago')}</p>
          <button type="submit" className="clase-page-cta" disabled={enviando || !calc}>
            {enviando ? <><Loader2 size={16} className="spin" /> {T('pagando')}</> : T('pagar')}
          </button>
        </form>
      </div>
    </main>
  );
}
